import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Gift, ArrowRight, Check, Lock } from "lucide-react";
import { useI18n } from "@/i18n";
import { trackGiftCardCheckout } from "@/lib/analytics";

export default function CarteCadeau() {
  const { t, lang, alternateUrls } = useI18n();
  useSEO({
    title: t("nav.giftcard"),
    description: lang === "en"
      ? "Offer an exceptional dining experience at L'Entracte, Ville d'Avray. Digital gift card from €25, sent instantly by email (PDF)."
      : "Offrez une expérience gastronomique d'exception à L'Entracte, Ville d'Avray. Carte cadeau dématérialisée à partir de 25€, envoyée par email.",
    keywords: lang === "en"
      ? "restaurant gift card, gastronomic gift, dining voucher Ville d'Avray, original gift idea"
      : "carte cadeau restaurant, cadeau gastronomique, bon cadeau Ville d'Avray, idée cadeau original",
    lang,
    alternates: alternateUrls(),
  });

  const [presets, setPresets] = useState([50, 100, 150, 200]);
  const [min, setMin] = useState(25);
  const [max, setMax] = useState(500);
  const [amountChoice, setAmountChoice] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    beneficiary_name: "",
    beneficiary_email: "",
    message: "",
    send_to_beneficiary: false,
  });
  const [status, setStatus] = useState({ loading: false, error: "" });

  useEffect(() => {
    api.get("/giftcards/config").then((r) => {
      if (r.data?.presets) setPresets(r.data.presets);
      if (r.data?.min) setMin(r.data.min);
      if (r.data?.max) setMax(r.data.max);
    }).catch(() => {});
  }, []);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const finalAmount = amountChoice === "custom" ? Number(customAmount) : Number(amountChoice);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "" });

    if (!finalAmount || finalAmount < min || finalAmount > max) {
      setStatus({ loading: false, error: `${t("giftcard.errorRange")} ${min}€ ${lang === "en" ? "and" : "et"} ${max}€.` });
      return;
    }
    if (form.send_to_beneficiary && !form.beneficiary_email) {
      setStatus({ loading: false, error: t("giftcard.errorBenefEmail") });
      return;
    }

    try {
      const payload = {
        amount: finalAmount,
        sender_name: form.sender_name.trim(),
        sender_email: form.sender_email.trim(),
        beneficiary_name: form.beneficiary_name.trim(),
        beneficiary_email: form.beneficiary_email.trim() || null,
        message: form.message.trim(),
        send_to_beneficiary: !!form.send_to_beneficiary,
        origin_url: window.location.origin,
      };
      const r = await api.post("/giftcards/checkout", payload);
      trackGiftCardCheckout(finalAmount);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("URL de paiement manquante");
      }
    } catch (err) {
      setStatus({
        loading: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <div data-testid="giftcard-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="overline">{t("giftcard.overline")}</p>
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">
            {t("giftcard.title1")}<br />
            <span className="italic text-[#B8956A]">{t("giftcard.title2")}</span>
          </h1>
          <p className="mt-8 text-[#697585] text-lg max-w-xl leading-relaxed">
            {t("giftcard.sub")}
          </p>

          <ul className="mt-10 space-y-4 max-w-lg">
            {t("giftcard.bullets").map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#203240]">
                <Check size={18} className="text-[#B8956A] mt-1" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 inline-flex items-center gap-3 text-xs text-[#697585] uppercase tracking-[0.2em]">
            <Lock size={14} className="text-[#B8956A]" />
            {t("giftcard.securePayment")}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="border border-[#D9D3C5] bg-[#FFFFFF] p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Gift size={24} className="text-[#B8956A]" />
              <h2 className="font-display text-3xl">{t("giftcard.formTitle")}</h2>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-8" data-testid="giftcard-form">
              <div>
                <p className="overline">{t("giftcard.amount")}</p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`giftcard-preset-${p}`}
                      onClick={() => { setAmountChoice(p); setCustomAmount(""); }}
                      className={`py-4 border text-lg font-display transition-colors ${
                        amountChoice === p
                          ? "border-[#B8956A] bg-[#B8956A]/10 text-[#203240]"
                          : "border-[#D9D3C5] text-[#697585] hover:border-[#B8956A]/50"
                      }`}
                    >
                      {p} €
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="giftcard-preset-custom"
                    onClick={() => setAmountChoice("custom")}
                    className={`py-4 border text-sm uppercase tracking-[0.18em] transition-colors ${
                      amountChoice === "custom"
                        ? "border-[#B8956A] bg-[#B8956A]/10 text-[#203240]"
                        : "border-[#D9D3C5] text-[#697585] hover:border-[#B8956A]/50"
                    }`}
                  >
                    {t("giftcard.other")}
                  </button>
                </div>
                {amountChoice === "custom" && (
                  <div className="mt-4">
                    <div className="relative">
                      <input
                        data-testid="giftcard-custom-amount"
                        type="number"
                        min={min}
                        max={max}
                        step="1"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder={`${t("giftcard.customPlaceholder")} (${min}-${max}€)`}
                        className="field pr-12"
                        required
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#B8956A] font-display text-lg">€</span>
                    </div>
                    <p className="text-xs text-[#697585] mt-2">{t("giftcard.customHint")} {min}€ {lang === "en" ? "and" : "et"} {max}€</p>
                  </div>
                )}
              </div>

              <div>
                <p className="overline">{t("giftcard.beneficiary")}</p>
                <div className="mt-4 space-y-4">
                  <input
                    data-testid="giftcard-beneficiary-name"
                    required
                    value={form.beneficiary_name}
                    onChange={onChange("beneficiary_name")}
                    placeholder={t("giftcard.beneficiaryName")}
                    className="field"
                    maxLength={80}
                  />
                  <textarea
                    data-testid="giftcard-message"
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder={t("giftcard.messagePlaceholder")}
                    className="field"
                    rows={3}
                    maxLength={200}
                  />
                </div>
              </div>

              <div>
                <p className="overline">{t("giftcard.yourInfo")}</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    data-testid="giftcard-sender-name"
                    required
                    value={form.sender_name}
                    onChange={onChange("sender_name")}
                    placeholder={t("giftcard.yourName")}
                    className="field"
                  />
                  <input
                    data-testid="giftcard-sender-email"
                    required
                    type="email"
                    value={form.sender_email}
                    onChange={onChange("sender_email")}
                    placeholder={t("giftcard.yourEmail")}
                    className="field"
                  />
                </div>
              </div>

              <div className="border border-[#D9D3C5] p-5 bg-[#F6F0E7]/40">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="giftcard-send-to-beneficiary"
                    checked={form.send_to_beneficiary}
                    onChange={onChange("send_to_beneficiary")}
                    className="mt-1 accent-[#B8956A]"
                  />
                  <span className="text-sm text-[#203240]">
                    {t("giftcard.sendDirectly")} <strong>{t("giftcard.sendDirectlyB")}</strong>.
                    <span className="block text-xs text-[#697585] mt-1">
                      {t("giftcard.sendDirectlyHint")}
                    </span>
                  </span>
                </label>
                {form.send_to_beneficiary && (
                  <input
                    data-testid="giftcard-beneficiary-email"
                    required
                    type="email"
                    value={form.beneficiary_email}
                    onChange={onChange("beneficiary_email")}
                    placeholder={t("giftcard.beneficiaryEmail")}
                    className="field mt-4"
                  />
                )}
              </div>

              {status.error && (
                <p data-testid="giftcard-error" className="text-sm text-red-500 border border-red-200 bg-red-50 p-3">
                  {status.error}
                </p>
              )}

              <div className="border-t border-[#D9D3C5] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <p className="overline text-[#697585]">{t("giftcard.totalToPay")}</p>
                  <p className="font-display text-4xl text-[#203240] mt-1">
                    {finalAmount && finalAmount >= min ? `${finalAmount} €` : "—"}
                  </p>
                </div>
                <button
                  type="submit"
                  data-testid="giftcard-submit"
                  disabled={status.loading || !finalAmount || finalAmount < min}
                  className="btn-primary w-full sm:w-auto justify-center disabled:opacity-50"
                >
                  {status.loading ? t("giftcard.redirecting") : t("giftcard.payCta")} <ArrowRight size={14} />
                </button>
              </div>

              <p className="text-xs text-[#697585]">
                {t("giftcard.terms")}
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
